import { useState } from 'react';

const serviceOptions = [
  'Paint Protection Film (PPF)',
  'Full Vehicle PPF',
  'Partial PPF (Front End)',
  'Ceramic Coating',
  'Window Tint',
  'Matte PPF / Color Change',
  'Windshield Protection Film',
  'Motorcycle PPF & Ceramic',
  'Radar Detector Installation',
  'Vehicle Storage',
  'PPF + Ceramic Combo Package',
  'Not Sure — Need Consultation',
];

export default function LeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => data.append(key, value.toString()));
    setLoading(true);
    fetch('https://readdy.ai/api/form/d6sn9urvrivh3q8km3r0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    }).then(() => { setSubmitted(true); setLoading(false); })
      .catch(() => setLoading(false));
  };

  return (
    <section className="relative py-0 overflow-hidden" id="lead-form">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[720px]">
        {/* Left dark */}
        <div className="relative flex flex-col justify-center px-10 lg:px-16 py-20 bg-[#050505]">
          <div className="absolute inset-0 bg-cover bg-center opacity-8"
            style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/f9f576361f46add7d7174524021ab772.jpg')" }} />
          <div className="relative z-10">
            <span className="text-[#FF6B35] text-xs font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
              <span className="w-6 h-px bg-[#FF6B35]" />
              Free Consultation
            </span>
            <h2 className="font-display font-black text-4xl lg:text-5xl text-white leading-tight mb-6">
              Protect Your
              <br />
              <span className="text-[#FF6B35]">Vehicle Today</span>
            </h2>
            <ul className="space-y-4 mb-10">
              {[
                'No-obligation free consultation',
                'Transparent, itemized pricing',
                'XPEL certified installation',
                'Lifetime warranty options',
                'Same-week appointments available',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#FF6B35] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-xs" />
                  </div>
                  <span className="text-white/75 text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <a href="tel:+18605550127" className="flex items-center gap-3 cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35] rounded-xl group-hover:bg-[#E5501A] transition-colors">
                <i className="ri-phone-fill text-white text-xl" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-widest">Prefer to call?</p>
                <p className="text-white font-bold text-xl">(860) 555-0127</p>
              </div>
            </a>
            {/* Brand logos */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-gray-600 text-xs uppercase tracking-widest mb-4">Certified &amp; Approved Installer</p>
              <div className="flex flex-wrap gap-3">
                {['XPEL', 'SunTek', 'Ceramic Pro'].map((brand) => (
                  <span key={brand} className="bg-white/8 border border-white/15 text-white/60 text-xs font-bold px-4 py-2 rounded-lg tracking-widest">{brand}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="bg-[#0C0C0C] border-l border-white/8 flex items-center px-10 lg:px-16 py-20">
          {submitted ? (
            <div className="w-full text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FF6B35]/15 rounded-full mx-auto mb-5">
                <i className="ri-checkbox-circle-fill text-[#FF6B35] text-4xl" />
              </div>
              <h3 className="font-display font-bold text-2xl text-white mb-2">Consultation Requested!</h3>
              <p className="text-gray-400 text-base max-w-xs mx-auto mb-6">We'll contact you within 24 hours to confirm your free consultation at our South Windsor studio.</p>
              <a href="tel:+18605550127"
                className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E5501A] text-white font-bold px-6 py-3 rounded-xl transition-colors whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm" /></span>
                Call Now
              </a>
            </div>
          ) : (
            <form data-readdy-form id="dbn-auto-quote-form" onSubmit={handleSubmit} className="w-full space-y-5">
              <div>
                <h3 className="font-display font-bold text-2xl text-white mb-1">Request a Free Quote</h3>
                <p className="text-gray-500 text-sm mb-6">We'll respond within 24 hours with a personalized recommendation.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-1.5">First Name *</label>
                  <input type="text" name="first_name" required placeholder="John"
                    className="w-full bg-[#111111] border border-white/15 focus:border-[#FF6B35] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-1.5">Last Name *</label>
                  <input type="text" name="last_name" required placeholder="Smith"
                    className="w-full bg-[#111111] border border-white/15 focus:border-[#FF6B35] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none transition-colors" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-1.5">Phone Number *</label>
                <input type="tel" name="phone" required placeholder="(860) 555-0000"
                  className="w-full bg-[#111111] border border-white/15 focus:border-[#FF6B35] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none transition-colors" />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-1.5">Email Address</label>
                <input type="email" name="email" placeholder="john@email.com"
                  className="w-full bg-[#111111] border border-white/15 focus:border-[#FF6B35] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none transition-colors" />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-1.5">Vehicle (Year, Make, Model)</label>
                <input type="text" name="vehicle" placeholder="e.g. 2024 BMW M3 Competition"
                  className="w-full bg-[#111111] border border-white/15 focus:border-[#FF6B35] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none transition-colors" />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-1.5">Service Interested In *</label>
                <select name="service" required
                  className="w-full bg-[#111111] border border-white/15 focus:border-[#FF6B35] rounded-lg px-4 py-3 text-sm text-white outline-none transition-colors cursor-pointer">
                  <option value="">Select a service...</option>
                  {serviceOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-1.5">Additional Details ({charCount}/500)</label>
                <textarea name="message" rows={3} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)}
                  placeholder="Tell us about your vehicle, goals, or any questions..."
                  className="w-full bg-[#111111] border border-white/15 focus:border-[#FF6B35] rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none transition-colors resize-none" />
                {charCount > 500 && <p className="text-red-500 text-xs mt-1">Must be 500 characters or less.</p>}
              </div>

              <button type="submit" disabled={loading || charCount > 500}
                className="w-full flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#E5501A] disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-colors whitespace-nowrap cursor-pointer">
                {loading
                  ? <><i className="ri-loader-4-line animate-spin" /> Submitting...</>
                  : <><span className="w-5 h-5 flex items-center justify-center"><i className="ri-send-plane-fill" /></span> Request Free Consultation</>
                }
              </button>
              <p className="text-gray-600 text-xs text-center">We respect your privacy. No spam, ever.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
